import { useState, useMemo, useRef, useEffect } from 'react'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'

interface CosmeticImageProps {
	name: string
	images: {
		icon?: string
		featured?: string
		lego?: { large?: string }
	}
	video?: string
	variants: VariantChannel[] | null
	selectedVariant?: string
	onImageClick: (imageSrc: string) => void
}

interface VariantOption {
	tag: string
	name: string
	image: string
}

interface VariantChannel {
	channel: string
	type: string
	options: VariantOption[]
}

export function CosmeticImage({
	name,
	images,
	video,
	variants,
	selectedVariant,
	onImageClick,
}: CosmeticImageProps) {
	const [isVideoLoaded, setIsVideoLoaded] = useState(false)
	const videoRef = useRef<HTMLVideoElement>(null)

	const currentImage = useMemo(() => {
		if (selectedVariant && variants) {
			const selectedOption = variants
				.flatMap(v => v.options)
				.find(o => o.name === selectedVariant)
			return selectedOption?.image
		}
		return images.lego?.large || images.featured || images.icon
	}, [selectedVariant, variants, images])

	useEffect(() => {
		if (videoRef.current) {
			videoRef.current.load()
		}
	}, [video])

	return (
		<div className="space-y-4">
			{video ? (
				<div className="relative">
					<video
						ref={videoRef}
						className="h-auto w-full"
						autoPlay
						loop
						muted
						playsInline
						poster={currentImage}
						onLoadedData={() => setIsVideoLoaded(true)}
					>
						<source src={video} type="video/webm" />
						Your browser does not support the video tag.
					</video>
					{!isVideoLoaded && (
						<img
							src={currentImage}
							alt={name}
							className="absolute inset-0 h-full w-full object-cover"
						/>
					)}
				</div>
			) : (
				<img
					alt={name}
					className="cursor-pointer rounded-lg border border-gray-200 object-cover"
					height="300"
					width="100%"
					src={currentImage || ''}
					style={{ aspectRatio: '300/300', objectFit: 'cover' }}
					onClick={() => currentImage && onImageClick(currentImage)}
				/>
			)}
		</div>
	)
}
